import { z } from 'zod';
import { BaseModel } from '../base.model';

export const USER_SETTINGS_SUBCOLLECTION = 'user-settings';

/**
 * Firestore path: agents/{agentDbId}/user-settings/{report_id}
 *
 * One document per viewer per report, holding how that viewer has arranged the report's grids.
 * The existing report-default-settings path carries no viewer, being the defaults a report
 * ships with rather than what one person did to it.
 */
export const getUserGridSettingsCollectionPath = (agentDbId: string): string =>
  ['agents', agentDbId, USER_SETTINGS_SUBCOLLECTION].join('/');

export enum ReportGridSettingsKeys {
  reportId = 'report_id',
  presets = 'presets',
  /** Which preset a report opens on; absent means it opens on what it ships with. */
  defaultPresetId = 'default_preset_id',
  version = 'version',
}

/**
 * What a viewer changed about one column, and nothing else.
 *
 * Every property here is optional because only the ones actually changed are stored. That is
 * the whole point of the format: a property nobody has touched is absent, so it keeps taking
 * whatever the grid currently declares. Changing a default in code therefore reaches everyone
 * who never overrode it, instead of being masked by a stored copy of the old value — which is
 * what a full state snapshot did, and why changing one had to discard every saved layout.
 */
export const reportGridColumnSettingsSchema = z.object({
  visible: z.boolean().optional(),
  width: z.number().nullable().optional(),
  visibleIndex: z.number().optional(),
  sortOrder: z.string().nullable().optional(),
  sortIndex: z.number().nullable().optional(),
  filterValue: z.unknown().optional(),
  filterValues: z.unknown().optional(),
  /**
   * Whether a header filter's values are the ones kept or the ones left out.
   *
   * Ticking "Select all" and then clearing a few names makes DevExtreme store the cleared names
   * with 'exclude'. Without this the names came back on their own, read as 'include' — the
   * table then showed exactly the rows the viewer had taken out.
   */
  filterType: z.enum(['include', 'exclude']).nullable().optional(),
  selectedFilterOperation: z.string().nullable().optional(),
});

export type ReportGridColumnSettings = z.infer<typeof reportGridColumnSettingsSchema>;

/** Settings outside the grid's own state, such as a table that pages itself. */
export const reportGridTableSettingsSchema = z.object({
  pageSize: z.number().optional(),
  loadedCount: z.number().optional(),
  sortField: z.string().optional(),
  sortDescending: z.boolean().optional(),
});

export type ReportGridTableSettings = z.infer<typeof reportGridTableSettingsSchema>;

export const reportGridSettingsEntrySchema = z.object({
  /** The grid's id within the report. */
  id: z.string(),
  columns: z.record(reportGridColumnSettingsSchema).optional(),
  pageSize: z.number().optional(),
  table: reportGridTableSettingsSchema.optional(),
});

export type ReportGridSettingsEntry = z.infer<typeof reportGridSettingsEntrySchema>;

/**
 * A named arrangement of a report a viewer can come back to.
 *
 * It carries no date range and no mode on purpose. A preset says how the page is arranged and
 * whom it is narrowed to, so the same one can be applied to whatever period is on screen
 * rather than dragging the period it happened to be saved in along with it.
 *
 * The viewer's own arrangement is stored separately and goes on saving itself as they work; a
 * preset changes only when they say so.
 */
export const reportGridPresetSchema = z.object({
  /** Stable across renames, so the page can say which preset is loaded. */
  id: z.string(),
  name: z.string(),
  hiddenColumns: z.array(z.string()).default([]),
  grids: z.array(reportGridSettingsEntrySchema).default([]),
  /** How many rows the movers open at, which is the viewer's choice rather than the grid's. */
  mgaTopCount: z.number().optional(),
  agentTopCount: z.number().optional(),
  /** The RMD and agency filter the page is narrowed by. */
  reportFilterValues: z.array(z.string()).default([]),
  /**
   * Absent while presets are a viewer's own.
   *
   * Declared now so that sharing one later is a value changing rather than every stored preset
   * needing a shape it does not have.
   */
  isShared: z.boolean().optional(),
  updatedAt: z.string().optional(),
});

export type ReportGridPreset = z.infer<typeof reportGridPresetSchema>;

/**
 * What a viewer has saved for one report.
 *
 * Only presets. The page used to store the arrangement a viewer happened to be looking at as
 * well, saving itself as they worked, and the two said different things about the same page:
 * the picker named one arrangement while the screen showed another, and a preset applied on
 * opening would have been overwritten by the stored one anyway. Nothing is written now unless
 * the viewer asks for it.
 */
export const reportGridSettingsDocumentSchema = z.object({
  [ReportGridSettingsKeys.reportId]: z.string(),
  /**
   * A list rather than a map keyed by id, because of how this is written.
   *
   * Saving merges, and a merge walks into nested maps key by key — so a preset that was
   * deleted could never be removed, and it would come back on the next load. An array is
   * replaced whole, so the document says exactly what the viewer has.
   */
  [ReportGridSettingsKeys.presets]: z.array(reportGridPresetSchema).default([]),
  [ReportGridSettingsKeys.defaultPresetId]: z.string().nullable().default(null),
  /**
   * Counts up on every write, and is how a page tells a change worth taking from one to ignore.
   *
   * Firestore says whether a write is still in flight, but not whose it was once it lands, so a
   * page watching its own document cannot tell the server confirming its write from somebody
   * else changing it. Comparing the contents was the workaround, and it kept failing in new
   * ways — a map's keys come back in another order, confirmations arrive out of turn — each
   * time letting an older document be written back over a newer one.
   *
   * A version settles it without comparing anything: a snapshot no newer than what this page
   * last wrote has nothing in it worth taking.
   */
  [ReportGridSettingsKeys.version]: z.number().default(0),
});

export type ReportGridSettingsDocumentDto = z.infer<typeof reportGridSettingsDocumentSchema>;

export class ReportGridSettings extends BaseModel {
  // Declared rather than initialised: this file is compiled by the reporting API as well, whose
  // strict settings otherwise refuse a required field no constructor sets.
  [ReportGridSettingsKeys.reportId]!: string;
  [ReportGridSettingsKeys.presets]?: ReportGridPreset[];
  [ReportGridSettingsKeys.defaultPresetId]?: string | null;
  [ReportGridSettingsKeys.version]?: number;
}
